import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/rest";
import { useTelemetryStore, getDisplayStatus } from "../stores/telemetryStore";
import { useSiteStore } from "../stores/siteStore";
import { useSiteId } from "../hooks/useSiteId";
import { useLiveCount } from "../hooks/useLiveCount";
import { formatLiveCount } from "./LiveCountBanner";
import styles from "./KpiStrip.module.css";

/** Forces a re-render every `ms` so the staleness-based count stays fresh. */
function useTick(ms: number) {
  const [, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function KpiStrip() {
  const SITE_ID = useSiteId();
  const { t, i18n } = useTranslation();
  useTick(5_000);
  // ADR 204: each card is gated by the capability that produces its data —
  // OEE needs telemetry sessions, fleet-online needs RTLS poses, alerts need
  // either pipeline. Ungated cards on an inventory-only site were permanent
  // "—"/"0" islands (and their queries polled a backend with nothing to say).
  const hasRtls = useSiteStore((s) => s.hasCapability("rtls"));
  const hasTelemetry = useSiteStore((s) => s.hasCapability("telemetry"));
  const showOee = hasTelemetry;
  const showFleet = hasRtls;
  // ADR 363: the live count is gated on live traffic, like the overlay's layer row —
  // it exists exactly while a sweep is streaming into the active warehouse.
  const liveCount = useLiveCount();
  // ADR 282: open alerts straight from the live store (WS-fed) — the same
  // record the beams draw, so the number and the beams can never disagree.
  const openAlerts = useTelemetryStore(
    (s) => Object.values(s.alerts).filter((a) => a.status === "open").length,
  );
  // Count machines actually live (seen within STALE_MS), consistent with the
  // Fleet and Network pages — not the raw roster size.
  const onlineCount = useTelemetryStore(
    (s) => Object.values(s.forklifts).filter((f) => getDisplayStatus(f) !== "offline").length,
  );

  const oee = useQuery({
    queryKey: ["oee-modular-today", SITE_ID],
    queryFn: () => api.getOeeModular(SITE_ID, todayStart(), new Date()),
    // Retry slower on error so the KPI recovers when the backend comes back up.
    refetchInterval: (q) => q.state.status === "error" ? 60_000 : 30_000,
    retry: 1,
    retryDelay: 3_000,
    enabled: showOee,
  });

  // Capability grading (#56/#61): a proxy-tier product is "OEE estimado"; with a
  // factor unmeasured there is no OEE at all — only Indicadores + opt-in composite.
  const grade = oee.data?.grade;
  const oeeLabel =
    grade === "OEE_ESTIMADO" ? t("kpi.oee.estimated")
    : grade === "INDICADORES" ? t("kpi.oee.indicators")
    : t("kpi.oee.today");
  const oeeValue = !oee.data
    ? null
    : grade === "INDICADORES"
      ? (oee.data.composite !== undefined
          ? `${fmtPct(oee.data.composite)} (${oee.data.compositeLabel})`
          : "—")
      : fmtPct(oee.data.oee);

  if (!showOee && !showFleet && !liveCount) return null;
  const liveValue = liveCount === null ? null
    : liveCount.total !== null && liveCount.pct !== null
      ? `${liveCount.visited.toLocaleString(i18n.language)} / ${liveCount.total.toLocaleString(i18n.language)}`
      : liveCount.visited.toLocaleString(i18n.language);

  return (
    <section className={styles.strip} aria-label="Indicadores principais">
      {liveCount && (
        <KpiCard
          label={t("liveCount.title")}
          value={liveValue}
          hint={formatLiveCount(liveCount, t, i18n.language, true)}
          loading={false}
        />
      )}
      <KpiCard label={t("kpi.alerts.open")} value={String(openAlerts)} loading={false} />
      {showOee && (
        <KpiCard
          label={oeeLabel}
          value={oeeValue}
          loading={oee.isLoading}
        />
      )}
      {showFleet && (
        <KpiCard
          label={t("kpi.fleet.online")}
          value={String(onlineCount)}
          loading={false}
        />
      )}
    </section>
  );
}

function KpiCard({ label, value, loading, hint }: {
  label: string; value: string | null; loading: boolean; hint?: string;
}) {
  return (
    <article className={styles.card}>
      <div className={styles.label}>{label}</div>
      {loading
        ? <span className={`skeleton ${styles.skeletonVal}`} aria-hidden />
        : <div className={styles.value}>{value ?? "—"}</div>
      }
      {hint && !loading && <div className={styles.hint}>{hint}</div>}
    </article>
  );
}

function fmtPct(x?: number): string {
  if (x === undefined || x === null || Number.isNaN(x)) return "—";
  return `${(x * 100).toFixed(1).replace(".", ",")}%`;
}

function todayStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
